import React, { useEffect, useState } from "react";
import {
  faChartBar,
  faCog,
  faFlask,
  faLock,
  faRocket,
  faSearch,
  faUser,
  faUserTag,
  faWrench,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/react";

import { CSSProvider } from "@perfectserve/css-styles-react";
import { MermaidDiagram } from "@perfectserve/library";
import type { ContextSelection } from "@perfectserve/ps-context-selector";
import { ContextSelector } from "@perfectserve/ps-context-selector";

import LibraryDemo from "../components/LibraryDemo";
import { useUser } from "../contexts/UserContext";
import { PendoConfig, pendoService } from "../services/pendoService";

import "./documentation.css";

const DocumentationPage = () => {
  const { user, selectedRole } = useUser();
  const [contextSelection, setContextSelection] = useState<ContextSelection>({
    accountIds: [],
    userIds: [],
    privilegeGroupIds: [],
    accounts: [],
    users: [],
    privilegeGroups: [],
  });
  const [pendoConfig, setPendoConfig] = useState<PendoConfig>();
  const [isLibraryDemoOpen, setIsLibraryDemoOpen] = useState(false);

  // Get current Pendo configuration
  useEffect(() => {
    if (user && selectedRole) {
      try {
        const config = pendoService.createPendoConfig(user, selectedRole);
        setPendoConfig(config);
      } catch (_error) {
        // Silently handle Pendo config errors
      }
    }
  }, [user, selectedRole]);

  const handleTestError = () => {
    throw new Error("Sentry Test Error - Break the World button clicked!");
  };

  const handleTestSentryCapture = () => {
    Sentry.captureException(new Error("Manual Sentry test error"));
    alert("Manual error sent to Sentry! Check your Sentry dashboard.");
  };

  const handleContextSelectionChange = (selection: ContextSelection) => {
    setContextSelection(selection);
  };

  return (
    <CSSProvider>
      <div className="documentation-container">
        {/* Platform Overview Section */}
        <div className="platform-overview-section">
          <div className="platform-overview-card">
            <div className="platform-overview-grid">
              {/* Left Column - Feature Sections */}
              <div className="platform-features-column">
                <div>
                  <div className="platform-description">
                    PS-Web is a monorepo designed to provide essential infrastructure components that every PerfectServe
                    application needs. It eliminates the need to rebuild authentication, permissions, API management,
                    and core UI patterns from scratch. This satisfies the PerfectServe side of API processing, and the
                    "http" package can be replaced in its entirety to save different schemes.
                  </div>

                  <div className="feature-section-title">Authentication & Security</div>
                  <ul>
                    <li>
                      <strong>OIDC Integration:</strong> Complete OpenID Connect flow
                    </li>
                    <li>
                      <strong>Token Management:</strong> Automatic refresh & secure storage
                    </li>
                    <li>
                      <strong>Permission System:</strong> Role-based access control
                    </li>
                    <li>
                      <strong>HTTP Interception:</strong> Automatic auth headers
                    </li>
                  </ul>
                </div>

                <div>
                  <div className="feature-section-title">Development Infrastructure</div>
                  <ul>
                    <li>
                      <strong>Environment Config:</strong> Runtime API endpoint management
                    </li>
                    <li>
                      <strong>Package Architecture:</strong> Modular, workspace-linked packages
                    </li>
                    <li>
                      <strong>CSS Framework:</strong> Consistent styling with encapsulation
                    </li>
                    <li>
                      <strong>Error Tracking:</strong> Sentry integration for monitoring
                    </li>
                    <li>
                      <strong>Docker Production:</strong> Containerized builds with FontAwesome Pro support
                    </li>
                  </ul>
                </div>

                <div>
                  <div className="feature-section-title">Navigation & Routing</div>
                  <ul>
                    <li>
                      <strong>Protected Routes:</strong> Permission-based navigation
                    </li>
                    <li>
                      <strong>Parameter Injection:</strong> Automatic route/query props
                    </li>
                    <li>
                      <strong>Dynamic Menus:</strong> Context-aware navigation
                    </li>
                    <li>
                      <strong>User Experience:</strong> Seamless role switching
                    </li>
                  </ul>
                </div>

                <div>
                  <div className="feature-section-title">Ready-to-Use Components</div>
                  <ul>
                    <li>
                      <strong>Context Selector:</strong> Multi-select accounts/users
                    </li>
                    <li>
                      <strong>FontAwesome Icons:</strong> Consistent icon system
                    </li>
                    <li>
                      <strong>Mermaid Diagrams:</strong> Dynamic flowcharts & visualizations
                    </li>
                    <li>
                      <strong>UI Library:</strong> Shared components & patterns
                    </li>
                    <li>
                      <strong>Analytics:</strong> Pendo integration for insights
                    </li>
                  </ul>
                </div>
              </div>

              {/* Right Column - Integrated Workflow Diagram */}
              <div>
                {/* Workflow Steps with Integrated Sections */}
                <div className="workflow-steps-container">
                  {/* Sentry Integration Section */}
                  <div className="sentry-integration-container">
                    <div className="sentry-step-label">
                      <FontAwesomeIcon icon={faWrench} className="auth-step-icon" />
                      Initialize Sentry
                    </div>
                    <div className="sentry-content-card">
                      <h4 className="sentry-card-title">Error Tracking (Sentry)</h4>
                      <p className="sentry-card-description">
                        Error tracking system initialized at application startup to monitor and log errors.
                      </p>

                      <div className="sentry-buttons-container">
                        <button type="button" onClick={handleTestError} className="sentry-button sentry-break-button">
                          Break the World
                        </button>
                        <button
                          type="button"
                          onClick={handleTestSentryCapture}
                          className="sentry-button sentry-test-button"
                        >
                          Test Manual Capture
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Authentication Flow Steps */}
                <div className="auth-flow-steps mt-30">
                  <div className="auth-step">
                    <FontAwesomeIcon icon={faLock} className="auth-step-icon" />
                    Receive OIDC Token → Decode Token
                  </div>
                  <div className="auth-step">
                    <FontAwesomeIcon icon={faUser} className="auth-step-icon" />
                    Extract IdentityId & UPN
                  </div>
                  <div className="auth-step">
                    <FontAwesomeIcon icon={faSearch} className="auth-step-icon" />
                    Call Directory API → Load User Profile
                  </div>
                  <div className="auth-step">
                    <FontAwesomeIcon icon={faUserTag} className="auth-step-icon" />
                    Check Available Roles → Role Selection
                  </div>
                  <div className="auth-step">
                    <FontAwesomeIcon icon={faCog} className="auth-step-icon" />
                    Initialize App Context → Load Permissions
                  </div>
                </div>

                {/* Pendo Analytics Section */}
                <div className="pendo-integration-container mt-30">
                  <div className="pendo-step-label">
                    <FontAwesomeIcon icon={faChartBar} className="auth-step-icon" />
                    Initialize Pendo Analytics
                  </div>
                  <div className="pendo-content-card">
                    <h4 className="pendo-card-title">Pendo Analytics Integration</h4>
                    <p className="pendo-card-description">
                      Analytics configuration generated after role selection for targeted insights.
                    </p>

                    {pendoConfig ? (
                      <div>
                        <h5 className="pendo-config-title">Current Pendo Configuration</h5>
                        <div className="pendo-config-container">
                          <pre className="pendo-config-code">{JSON.stringify(pendoConfig, null, 2)}</pre>
                        </div>
                      </div>
                    ) : (
                      <div className="pendo-no-config">
                        Pendo configuration not available. Please ensure you are logged in and have selected a role.
                      </div>
                    )}
                  </div>
                </div>
                {/* Final Steps */}
                <div className="workflow-final-step mt-30">
                  <FontAwesomeIcon icon={faRocket} className="final-step-icon" />
                  Setup API Interceptors → Application Ready
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Library Demonstration Button */}
      <div style={{ textAlign: "center", margin: "30px 0" }}>
        <button
          onClick={() => setIsLibraryDemoOpen(true)}
          style={{
            backgroundColor: "#374B94",
            color: "white",
            border: "none",
            padding: "12px 24px",
            borderRadius: "6px",
            fontSize: "16px",
            fontWeight: "600",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#2a3a75";
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.15)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#374B94";
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
          }}
        >
          <FontAwesomeIcon icon={faFlask} />
          Library Demonstration
        </button>
      </div>

      {/* FontAwesome Section */}
      <div className="fontawesome-section">
        <div className="fontawesome-card">
          <h2 className="fontawesome-title">FontAwesome Icons</h2>
          <p>Browse available icons and implementation guide.</p>

          <div className="fontawesome-library-link">
            <strong>Icon Library:</strong>{" "}
            <a
              href="https://fontawesome.com/icons"
              target="_blank"
              rel="noopener noreferrer"
              className="fontawesome-link"
            >
              https://fontawesome.com/icons
            </a>
          </div>

          <div>
            <strong>Implementation:</strong>
            <pre className="fontawesome-code-block">
              {`import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faSearch } from "@fortawesome/pro-solid-svg-icons";

<FontAwesomeIcon icon={faUser} />
<FontAwesomeIcon icon={faSearch} className="mr-10" />`}
            </pre>
          </div>

          <div className="fontawesome-note">
            <strong>Note:</strong> This project uses FontAwesome Pro. Available icon styles:
            <ul>
              <li>
                <code>@fortawesome/pro-solid-svg-icons</code> - Solid style icons
              </li>
              <li>
                <code>@fortawesome/pro-regular-svg-icons</code> - Regular style icons
              </li>
            </ul>
            <p>
              <strong>Installation:</strong> FontAwesome Pro requires authentication via <code>.npmrc</code> file with
              your Pro license token.
            </p>
          </div>
        </div>
      </div>

      {/* Context Selector Section */}
      <div className="context-selector-section">
        <div className="context-selector-card">
          <h2 className="context-selector-title">Context Selector Component</h2>
          <p className="context-selector-description">
            Search and select accounts, users, and privilege groups with multi-selection support.
          </p>

          <div className="context-selector-demo-container">
            <div className="context-selector-demo-section">
              <h3 className="context-selector-demo-title">Live Demo</h3>
              <ContextSelector
                selectedAccountIds={contextSelection.accountIds}
                selectedUserIds={contextSelection.userIds}
                selectedPrivilegeGroupIds={contextSelection.privilegeGroupIds}
                onSelectionChange={handleContextSelectionChange}
              />
            </div>

            <div className="context-selector-demo-section">
              <h3 className="context-selector-demo-title">Current Selection Output</h3>
              <div className="context-selector-output">
                <div>Account IDs: [{contextSelection.accountIds.map((id) => `"${id}"`).join(", ")}]</div>
                <div>User IDs: [{contextSelection.userIds.map((id) => `"${id}"`).join(", ")}]</div>
                <div>Privilege Group IDs: [{contextSelection.privilegeGroupIds.map((id) => `"${id}"`).join(", ")}]</div>

                {contextSelection.accounts.length > 0 && (
                  <div className="context-selector-output-section">
                    <div className="context-selector-output-title">Selected Accounts:</div>
                    {contextSelection.accounts.map((account, index) => (
                      <div key={account.Id} className="context-selector-output-item">
                        {index + 1}. {account.AccountName} ({account.AccountType})
                      </div>
                    ))}
                  </div>
                )}

                {contextSelection.users.length > 0 && (
                  <div className="context-selector-output-section">
                    <div className="context-selector-output-title">Selected Users:</div>
                    {contextSelection.users.map((user, index) => (
                      <div key={user.Id} className="context-selector-output-item">
                        {index + 1}. {user.DisplayName} - {user.AccountName} ({user.Stage})
                      </div>
                    ))}
                  </div>
                )}

                {contextSelection.privilegeGroups.length > 0 && (
                  <div className="context-selector-output-section">
                    <div className="context-selector-output-title">Selected Privilege Groups:</div>
                    {contextSelection.privilegeGroups.map((group, index) => (
                      <div key={group.GroupId} className="context-selector-output-item">
                        {index + 1}. {group.Name} ({group.GroupId})
                      </div>
                    ))}
                  </div>
                )}

                {contextSelection.accountIds.length === 0 &&
                  contextSelection.userIds.length === 0 &&
                  contextSelection.privilegeGroupIds.length === 0 && (
                    <div className="context-selector-no-selection">
                      No selections yet. Try searching for accounts, users, or privilege groups.
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mermaid Diagram Section */}
      <div className="mermaid-section">
        <div className="mermaid-card">
          <h2 className="mermaid-title">Mermaid Diagram Component</h2>
          <p className="mermaid-description">
            Create dynamic flowcharts, sequence diagrams, and other visualizations using Mermaid syntax.
          </p>

          <div className="mermaid-implementation">
            <strong>Implementation:</strong>
            <pre className="mermaid-code-block">
              {`import { MermaidDiagram } from "@perfectserve/library";

<MermaidDiagram
  title="My Flowchart"
  code={\`flowchart TD
    A[Start] --> B{Decision}
    B -->|Yes| C[Action 1]
    B -->|No| D[Action 2]
    C --> E[End]
    D --> E\`}
  style={{ margin: "20px 0" }}
/>`}
            </pre>
          </div>

          <div className="mermaid-note">
            <strong>Features:</strong>
            <ul>
              <li>Lazy loading for optimal performance</li>
              <li>Error handling with fallback display</li>
              <li>Support for all Mermaid diagram types</li>
              <li>Customizable styling and theming</li>
            </ul>
            <p>
              <strong>Documentation:</strong> Visit{" "}
              <a href="https://mermaid.js.org/" target="_blank" rel="noopener noreferrer" className="mermaid-link">
                mermaid.js.org
              </a>{" "}
              for complete syntax reference.
            </p>
          </div>
        </div>
      </div>

      <div>
        <MermaidDiagram
          title="Application Startup & Authentication Flow"
          code={`flowchart TD
    A[Application Startup<br/>Load index.tsx] --> B[Initialize Sentry]
    B --> E[Initialize Auth Provider<br/>Check for existing tokens]
    E --> F{Callback URL<br/>with tokens?}
    F -->|Yes| G[Handle OIDC Callback<br/>Process tokens]
    F -->|No| H[Check stored auth<br/>authService.isAuthenticated]
    G --> I[Set isAuthenticated = true]
    H --> I
    I --> J[Initialize User Provider<br/>Wait for authentication]
    J --> K[Extract IdentityId from ID Token]
    K --> L[Call Directory API<br/>GET /directory/byIdentityId]
    L --> M[Load User Profile & Roles<br/>Create UserConfiguration]
    M --> N{Check Available Roles}
    N -->|Multiple Roles| O[Show Role Selection Modal]
    N -->|Single Role| P[Auto-select Role]
    O --> Q[User Selects Role]
    Q --> R[Initialize App Context]
    P --> R
    R --> S[Initialize Pendo Analytics<br/>Setup user tracking]
    S --> T[Load Role-specific Permissions]
    T --> U[Application Ready<br/>Show Navigation & Routes]

    style A fill:#e1f5fe
    style U fill:#e8f5e8
    style O fill:#fff3e0
    style F fill:#f3e5f5
    style N fill:#f3e5f5`}
          style={{ margin: "0" }}
        />
      </div>

      {/* Library Demo Modal */}
      <LibraryDemo isOpen={isLibraryDemoOpen} onClose={() => setIsLibraryDemoOpen(false)} />
    </CSSProvider>
  );
};

export default DocumentationPage;
